<template>
    <div class="bg-white border border-border-primary rounded-lg shadow-xl h-[400px] w-[320px] overflow-hidden flex flex-col">
        <!-- Search Input -->
        <div class="p-3 border-b border-border-primary">
            <TextInput
                v-model="searchQuery"
                placeholder="Search companies..."
                leading-icon="magnifying-glass"
                autofocus
                class="border-none"
            />
        </div>

        <!-- Companies Column -->
        <div class="flex flex-1 overflow-hidden">
            <NavigationDropdownColumn title="Companies" class="w-80" highlight>
                <div v-if="displayedCompanies.length === 0" class="p-4 text-center text-text-secondary paragraph-md">
                    No results found
                </div>
                <NavigationDropdownList v-else>
                    <NavigationDropdownItem
                        v-for="(company, index) in displayedCompanies"
                        :key="company.id"
                        :label="company.name ?? 'Untitled'"
                        :to="companyRoutePreserving(company)"
                        :current="company.id === currentCompanyId"
                        :selected="index === focusedIndex"
                        @mousemove="focusedIndex = index"
                        @click="() => selectCompany(company)"
                    />
                </NavigationDropdownList>
            </NavigationDropdownColumn>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import TextInput from '@/vue/components/design-system/input/TextInput.vue'
import { useBreadcrumbsStore } from '@/vue/stores/breadcrumbsStore'
import type { BreadcrumbCompany } from '../breadcrumb.types'
import { useBreadcrumb_experimental } from '../use-breadcrumbs'
import { useCompanyHierarchy } from '../use-company-hierarchy'
import { useCompanyList } from '../use-company-list'
import { useKeyboardNavigation } from '../use-keyboard-navigation'
import { useNavigationRoutes } from '../use-navigation-routes'
import { useNavigationSearch } from '../use-navigation-search'
import NavigationDropdownColumn from './ui/navigation-dropdown-column.vue'
import NavigationDropdownItem from './ui/navigation-dropdown-item.vue'
import NavigationDropdownList from './ui/navigation-dropdown-list.vue'

defineProps<{
    isOpen: boolean
}>()

const emit = defineEmits<{
    'update:isOpen': [value: boolean]
}>()

const router = useRouter()
const breadcrumbsStore = useBreadcrumbsStore()
const { companies } = useCompanyList()
const { setSelectedCompany } = useCompanyHierarchy()
const { company } = useBreadcrumb_experimental()
const { companyRoutePreserving } = useNavigationRoutes()

// Current company from route/breadcrumb
const currentCompanyId = computed(() => company.value?.id || null)

// Search functionality
const searchQuery = ref('')
const { isSearching, searchResults } = useNavigationSearch(companies, searchQuery)

// Display either search results or all companies
const displayedCompanies = computed(() => {
    return isSearching.value ? searchResults.value.map(result => result.item) : companies.value
})

function closeDropdown() {
    emit('update:isOpen', false)
}

async function selectCompany(company: BreadcrumbCompany) {
    closeDropdown()
    // TODO: This is a hack to make the old and new breadcrumb navigation work.
    // It is needed because we don't actually use routes for switching between companies.
    // Instead currently selected company is set in the breadcrumbs store.
    // To be removed once the old breadcrumb navigation is removed.
    breadcrumbsStore.setCompanyId(company.id)
    await setSelectedCompany(company.id)
}

// Keyboard navigation
const { focusedIndex } = useKeyboardNavigation({
    items: displayedCompanies,
    searchQuery,
    onSelect: async (company: BreadcrumbCompany) => {
        await selectCompany(company)
        router.push(companyRoutePreserving(company))
    },
})
</script>
